import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TelegramIcon from "@mui/icons-material/Telegram";
import EmailIcon from "@mui/icons-material/Email";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsPhoneIcon from "@mui/icons-material/SettingsPhone";
import MusicVideoIcon from "@mui/icons-material/MusicVideo";
import SmsIcon from "@mui/icons-material/Sms";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GoogleIcon from "@mui/icons-material/Google";
import GroupsIcon from "@mui/icons-material/Groups";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const channelConfig = {
  whatsapp_oficial: { icon: WhatsAppIcon, color: "#25D366", label: "WhatsApp" },
  facebook: { icon: FacebookIcon, color: "#1877F2", label: "Facebook" },
  instagram: { icon: InstagramIcon, color: "#E4405F", label: "Instagram" },
  telegram: { icon: TelegramIcon, color: "#0088CC", label: "Telegram" },
  email: { icon: EmailIcon, color: "#FF6B00", label: "E-mail" },
  voip: { icon: PhoneInTalkIcon, color: "#8B5CF6", label: "VoIP" },
  sip: { icon: SettingsPhoneIcon, color: "#06B6D4", label: "SIP Trunk" },
  tiktok: { icon: MusicVideoIcon, color: "#000000", label: "TikTok" },
  sms: { icon: SmsIcon, color: "#43A047", label: "SMS" },
  google_business: { icon: GoogleIcon, color: "#4285F4", label: "Google Business" },
  mercadolivre: { icon: StorefrontIcon, color: "#FFE600", label: "Mercado Livre" },
  teams: { icon: GroupsIcon, color: "#6264A7", label: "Microsoft Teams" },
};

const statusConfig = {
  CONNECTED: { color: "#25D366", bg: "rgba(37, 211, 102, 0.1)", label: "Conectado" },
  DISCONNECTED: { color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)", label: "Desconectado" },
  CONNECTING: { color: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)", label: "Conectando..." },
  ERROR: { color: "#EF4444", bg: "rgba(239, 68, 68, 0.1)", label: "Erro" },
};

export default function ConnectionsList({
  connections,
  onDisconnect,
  onReconnect,
  onDelete,
  onRefresh,
  onOpenHub,
  onOpenVoIP,
}) {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, connection: null });

  const handleDeleteClick = (conn) => {
    setDeleteDialog({ open: true, connection: conn });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.connection) {
      onDelete(deleteDialog.connection.id);
    }
    setDeleteDialog({ open: false, connection: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, connection: null });
  };

  if (connections.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 12 }}>
        <LinkOffIcon sx={{ fontSize: 56, color: "#374151", mb: 2 }} />
        <Typography variant="h6" sx={{ color: "#6B7280", fontWeight: 600 }}>
          Nenhuma conexao ativa
        </Typography>
        <Typography variant="body2" sx={{ color: "#4B5563", mb: 3 }}>
          Conecte seus canais de comunicacao
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={onOpenHub}
          sx={{
            bgcolor: "#FF6B00",
            "&:hover": { bgcolor: "#E06000" },
          }}
        >
          Conectar Canal
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#F3F4F6" }}>
          Conexoes
          <Chip
            label={connections.length}
            size="small"
            sx={{
              ml: 1.5,
              bgcolor: "rgba(255, 107, 0, 0.15)",
              color: "#FF6B00",
              fontWeight: 700,
              fontSize: "0.75rem",
              height: 22,
            }}
          />
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={onRefresh} sx={{ color: "#6B7280" }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {connections.map((conn) => {
          const channel = channelConfig[conn.channel] || channelConfig.email;
          const status = statusConfig[conn.status] || statusConfig.ERROR;
          const ChannelIcon = channel.icon;
          const isConnected = conn.status === "CONNECTED" && conn.active;

          return (
            <Grid item xs={12} sm={6} md={4} key={conn.id}>
              <Card
                sx={{
                  bgcolor: "#1E293B",
                  border: "1px solid rgba(255, 255, 255, 0.04)",
                  borderRadius: "12px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    border: `1px solid ${channel.color}30`,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: `${channel.color}15`,
                          flexShrink: 0,
                        }}
                      >
                        <ChannelIcon sx={{ color: channel.color, fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 600, color: "#F3F4F6", lineHeight: 1.3 }}
                        >
                          {conn.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {channel.label}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Botoes de acao */}
                    <Box sx={{ display: "flex", gap: 0.25 }}>
                      {isConnected ? (
                        <Tooltip title="Desconectar">
                          <IconButton
                            size="small"
                            onClick={() => onDisconnect(conn.id)}
                            sx={{ color: "#4B5563", "&:hover": { color: "#EF4444" } }}
                          >
                            <PowerSettingsNewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reconectar">
                          <IconButton
                            size="small"
                            onClick={() => onReconnect(conn.id)}
                            sx={{ color: "#4B5563", "&:hover": { color: "#25D366" } }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(conn)}
                          sx={{ color: "#4B5563", "&:hover": { color: "#EF4444" } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {(conn.channel === "voip" || conn.channel === "sip") && onOpenVoIP && (
                        <Tooltip title="Painel VoIP">
                          <IconButton
                            size="small"
                            onClick={() => onOpenVoIP(conn.id)}
                            sx={{ color: "#4B5563", "&:hover": { color: "#8B5CF6" } }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        bgcolor: status.bg,
                        color: status.color,
                        fontSize: "0.7rem",
                        height: 22,
                        fontWeight: 600,
                        border: "none",
                      }}
                    />
                    {conn.phoneNumber && (
                      <Typography variant="caption" sx={{ color: "#6B7280" }}>
                        {conn.phoneNumber}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dialog de confirmacao para remover */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            bgcolor: "#1E293B",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "12px",
            minWidth: 360,
          },
        }}
      >
        <DialogTitle sx={{ color: "#F3F4F6", fontWeight: 600, pb: 1 }}>
          Remover conexao?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#9CA3AF" }}>
            A conexao <strong style={{ color: "#F3F4F6" }}>{deleteDialog.connection?.name}</strong> sera
            removida permanentemente. Esta acao nao pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{ color: "#9CA3AF", textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
